import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronLeft, Shield, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function PrivacySettings() {
  const navigate = useNavigate();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("New passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current: cur, next });
      toast.success("Password updated");
      setCur(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
      <button onClick={() => navigate("/profile")} className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-profile">
        <ChevronLeft size={14} /> Account
      </button>
      <p className="eyebrow mt-6 flex items-center gap-2"><Shield size={12} /> Stylebay · Privacy</p>
      <h1 className="editorial-h1 mt-3">Privacy &amp; Security.</h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-md">
        Update your password and control your data.
      </p>

      <form onSubmit={changePassword} className="mt-10 border border-border p-6 lg:p-8 space-y-5" data-testid="password-form">
        <p className="eyebrow">Change password</p>
        <div className="space-y-2">
          <Label className="eyebrow">Current password</Label>
          <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="rounded-none" data-testid="password-current" />
        </div>
        <div className="space-y-2">
          <Label className="eyebrow">New password</Label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="rounded-none" data-testid="password-new" />
        </div>
        <div className="space-y-2">
          <Label className="eyebrow">Confirm new password</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-none" data-testid="password-confirm" />
        </div>
        <Button
          type="submit"
          disabled={busy || !cur || !next || !confirm}
          className="rounded-none uppercase tracking-[0.2em] text-xs h-11 px-6"
          data-testid="password-save-btn"
        >
          {busy ? <><Loader2 size={14} className="animate-spin mr-2" />Saving</> : <><Check size={14} className="mr-2" />Update password</>}
        </Button>
      </form>

      <div className="mt-8 border border-dashed border-border p-6">
        <p className="eyebrow">Data controls</p>
        <p className="text-sm mt-3">Request a copy of your wardrobe, saved looks, and posts.</p>
        <Button
          variant="outline"
          className="mt-4 rounded-none uppercase tracking-[0.2em] text-[11px] h-10"
          onClick={() => toast.success("Data export queued — we'll email it within 24h")}
          data-testid="export-data-btn"
        >
          Request data export
        </Button>
      </div>
    </div>
  );
}
